import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";

// Composite-key entity: ActionBank rows are keyed by (message_id, bank_id),
// so PUT/DELETE expose both as path params. The dedicated /action-banks/
// endpoints in LiftCloud were added so the wizard's Actions step can
// CRUD action_bank rows without round-tripping the entire bank update
// payload through /banks/:bankID.

const maxRetries = 2;

const baseQuery = retry(
  createFlatBufferBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? ""),
  { maxRetries },
);

export const actionBankAPI = createApi({
  reducerPath: "actionBankAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetActionBanks"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* GET LIST */
    getActionBanks: builder.query({
      query: (body: any) => ({
        url: `/action-banks/?page=${body.page ?? 1}&limit=${body.limit ?? 100}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetActionBanks"],
    }),
    //* GET BY COMPOSITE KEY */
    getActionBank: builder.query({
      query: (body: any) => ({
        url: `/action-banks/${body.messageId}/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetActionBanks"],
    }),
    //* GET BY BANK */
    getActionBanksByBank: builder.query({
      query: (body: any) => ({
        url: `/action-banks/by-bank/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetActionBanks"],
    }),
    //* GET BY ORG */
    getActionBanksByOrganization: builder.query({
      query: (body: any) => ({
        url: `/action-banks/by-organization/${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetActionBanks"],
    }),
    //* CREATE */
    postActionBank: builder.mutation({
      query: (body: any) => ({
        url: `/action-banks/`,
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateActionBank" },
      invalidatesTags: ["tagGetActionBanks"],
    }),
    //* UPDATE (composite key in URL) */
    putActionBank: builder.mutation({
      query: (body: any) => ({
        url: `/action-banks/${body.messageId}/${body.bankId}`,
        method: "PUT",
        // Path-level keys win the spread — same belt-and-suspenders
        // pattern used by putUser / putRole. The UpdateActionBank
        // FlatBuffer serializer needs both message_id and bank_id at
        // pack time, so guaranteeing them here keeps callers free of
        // worrying whether body.body includes them.
        body: { ...body.body, message_id: body.messageId, bank_id: body.bankId },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateActionBank" },
      invalidatesTags: ["tagGetActionBanks"],
    }),
    //* DELETE (composite key in URL) */
    deleteActionBank: builder.mutation({
      query: (body: any) => ({
        url: `/action-banks/${body.messageId}/${body.bankId}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetActionBanks"],
    }),
  }),
});

export const {
  useGetActionBanksQuery,
  useLazyGetActionBanksQuery,
  useLazyGetActionBankQuery,
  useGetActionBanksByBankQuery,
  useLazyGetActionBanksByBankQuery,
  useLazyGetActionBanksByOrganizationQuery,
  usePostActionBankMutation,
  usePutActionBankMutation,
  useDeleteActionBankMutation,
} = actionBankAPI;
